import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, CircleDot, Circle, Briefcase, CheckCircle2, AlertOctagon, Clock, Activity, Users, Timer, Target, TrendingUp } from "lucide-react";
import { EmployeeAttendance } from "@/pages/EmployeeErp";

/**
 * Detailed line-manager view of one direct report:
 *  – Task rollup (open / done / overdue) + 30-day completion rate.
 *  – Weekly time-on-platform + clock-in counts (last 8 weeks).
 *  – Today's attendance status.
 *  – Recent tasks assigned to them AND tasks they assigned to others.
 *  – Recent activity log.
 * All data comes from GET /me/direct-reports/{uid} — a single, authorised
 * endpoint that enforces the reporting relationship.
 */
export default function EmployeeDirectReportDetail() {
  const { uid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/me/direct-reports/${uid}`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setErr(e?.response?.data?.detail || "Couldn't load report"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [uid]);

  const fmtHrs = (sec) => (sec ? `${(sec / 3600).toFixed(1)}h` : "0h");

  if (loading) return <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>;
  if (err) return <div className="kb-card p-8 text-rose-700">{err}</div>;
  if (!data) return null;

  const u = data.user;

  return (
    <div data-testid="direct-report-detail-page">
      <Link to="/dashboard/employee/direct-reports" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to Direct Reports
      </Link>

      <div className="kb-card p-5 mt-3 flex flex-wrap items-center gap-4">
        {u.picture ? <img src={u.picture} alt="" className="w-14 h-14 rounded-full object-cover" /> :
          <div className="w-14 h-14 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-lg text-kb-primary">{u.name?.[0]?.toUpperCase()}</div>}
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-2xl text-kb-ink">{u.name}</h1>
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
            {u.role}{u.employee_role ? ` · ${u.employee_role}` : ""}{u.team_section ? ` · ${u.team_section}` : ""}
          </div>
          <div className="text-xs text-kb-ink-muted">{u.email}</div>
        </div>
        <div className="kb-mono text-[10px] uppercase tracking-wider flex items-center gap-2">
          {data.attendance_today.clocked_in ? (
            <span className="inline-flex items-center gap-1 text-emerald-700"><CircleDot className="w-3 h-3" /> Clocked in</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-kb-ink-muted"><Circle className="w-3 h-3" /> Not clocked in</span>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-3 mt-6 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={Briefcase} label="Open" value={data.task_stats.open} tone="ink" />
        <MetricCard icon={CheckCircle2} label="Done" value={data.task_stats.done} tone="emerald" />
        <MetricCard icon={AlertOctagon} label="Overdue" value={data.task_stats.overdue} tone="rose" />
        <MetricCard icon={Activity} label="Completion (30d)" value={`${data.completion_rate_30d}%`} sub={`${data.completed_last_30d}/${data.created_last_30d}`} tone="indigo" />
      </div>

      {/* Weekly time on platform */}
      <div className="kb-card p-5 mt-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> WEEKLY TIME ON PLATFORM</div>
        {data.weekly_time_on_platform.length === 0 ? (
          <div className="text-xs text-kb-ink-muted mt-3">No attendance events in the last 8 weeks.</div>
        ) : (
          <table className="w-full mt-3 text-sm">
            <thead>
              <tr className="text-left kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted border-b border-slate-200">
                <th className="py-2">Week</th>
                <th className="py-2">Clock-ins</th>
                <th className="py-2">Clock-outs</th>
                <th className="py-2">Time on platform</th>
              </tr>
            </thead>
            <tbody>
              {data.weekly_time_on_platform.map((w) => (
                <tr key={w.week} className="border-b border-slate-100">
                  <td className="py-2 kb-mono text-xs">{w.week}</td>
                  <td className="py-2">{w.in_events}</td>
                  <td className="py-2">{w.out_events}</td>
                  <td className="py-2">{fmtHrs(w.seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Full daily attendance — reuses the same component the user's own
          /dashboard/employee/attendance page renders, passing `userId` so the
          backend returns this direct report's clock-in / clock-out sessions.
      */}
      <div className="kb-card p-5 mt-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2 mb-3">
          <Timer className="w-3.5 h-3.5" /> DAILY ATTENDANCE (LAST 30 DAYS)
        </div>
        <EmployeeAttendance userId={uid} embedded />
      </div>

      {/* Advanced analytics — compact derived signals so a manager can spot
          workload / consistency issues without leaving this page. */}
      <AdvancedAnalytics data={data} />

      {/* Tasks split */}
      <div className="grid gap-4 mt-6 lg:grid-cols-2">
        <TasksList title="Tasks assigned to them" tasks={data.assigned_to_them} />
        <TasksList title="Tasks they assigned to others" icon={Users} tasks={data.assigned_by_them} />
      </div>

      {/* Recent activity */}
      {data.recent_activity.length > 0 && (
        <div className="kb-card p-5 mt-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">RECENT ACTIVITY</div>
          <ul className="space-y-1.5 text-xs">
            {data.recent_activity.map((a, i) => (
              <li key={i} className="text-kb-ink-muted">
                <span className="kb-mono text-[10px] text-kb-ink">{new Date(a.at).toLocaleString()}</span> — {a.type || a.action || "event"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, tone }) {
  const toneMap = {
    ink: "text-kb-ink", emerald: "text-emerald-700", rose: "text-rose-700", indigo: "text-indigo-700",
  };
  return (
    <div className="kb-card p-4">
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`font-serif text-3xl mt-1 ${toneMap[tone] || "text-kb-ink"}`}>{value}</div>
      {sub && <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">{sub}</div>}
    </div>
  );
}

function TasksList({ title, tasks, icon: Icon = Briefcase }) {
  return (
    <div className="kb-card p-5">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" /> {title} · {tasks.length}
      </div>
      {tasks.length === 0 ? (
        <div className="text-xs text-kb-ink-muted mt-3">Nothing here.</div>
      ) : (
        <ul className="mt-3 space-y-2">
          {tasks.slice(0, 15).map((t) => (
            <li key={t.id} className="border-l-2 border-l-slate-200 pl-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-kb-ink truncate">{t.title}</span>
                <span className={`kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${t.status === "done" ? "bg-emerald-50 text-emerald-700" : t.status === "in_progress" ? "bg-indigo-50 text-indigo-700" : "bg-slate-50 text-kb-ink-muted"}`}>
                  {t.status}
                </span>
              </div>
              <div className="kb-mono text-[10px] text-kb-ink-muted">
                {t.due_at ? `Due ${new Date(t.due_at).toLocaleDateString()}` : "No due date"}{t.priority ? ` · ${t.priority}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Derived signals on top of the raw counters returned by the API — useful
 *  to compare direct reports at a glance (avg per week, on-time streak, task
 *  velocity). Kept side-effect-free so we can slot it under any dashboard. */
function AdvancedAnalytics({ data }) {
  const weeks = data.weekly_time_on_platform || [];
  const totalSec = weeks.reduce((s, w) => s + (w.seconds || 0), 0);
  const avgHrs = weeks.length ? (totalSec / 3600 / weeks.length) : 0;
  const activeWeeks = weeks.filter((w) => (w.seconds || 0) > 0).length;
  const consistency = weeks.length ? Math.round((activeWeeks / weeks.length) * 100) : 0;
  const bestWeek = weeks.reduce((b, w) => (w.seconds || 0) > (b.seconds || 0) ? w : b, {});
  const openTasks = data.task_stats?.open || 0;
  const overdue = data.task_stats?.overdue || 0;
  const overdueRate = openTasks > 0 ? Math.round((overdue / openTasks) * 100) : 0;
  const velocity = weeks.length ? Math.round((data.completed_last_30d || 0) / Math.max(1, weeks.length)) : (data.completed_last_30d || 0);
  return (
    <div className="mt-6">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">ADVANCED ANALYTICS</div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={TrendingUp} label="Avg hrs / week (8w)" value={avgHrs.toFixed(1)} sub={`${activeWeeks}/${weeks.length || 0} active weeks`} tone="indigo" />
        <MetricCard icon={Target} label="Consistency" value={`${consistency}%`} sub="Weeks with any clock-in" tone="emerald" />
        <MetricCard icon={AlertOctagon} label="Overdue rate" value={`${overdueRate}%`} sub={`${overdue}/${openTasks} open tasks`} tone={overdueRate > 30 ? "rose" : "ink"} />
        <MetricCard icon={Activity} label="Weekly task velocity" value={velocity} sub={`Best week ${bestWeek.week || "—"}${bestWeek.seconds ? ` · ${(bestWeek.seconds / 3600).toFixed(1)}h` : ""}`} tone="indigo" />
      </div>
    </div>
  );
}

